import { rowHints } from "../row-hints"
import type { LoadListTableRow } from "../load-list-row-model"

export type AddressTableFilters = {
  search: string
  status: "all" | "review" | "misconfigured"
  numberGreaterThan: string
}

const normalizeSearch = (value: string) =>
  value.trim().replace(/\s+/g, " ").toLocaleLowerCase("de-DE")

export const filterAddressRows = <TRow extends LoadListTableRow>({
  rows,
  filters,
}: {
  rows: TRow[]
  filters: AddressTableFilters
}): TRow[] => {
  const searchTerms = normalizeSearch(filters.search).split(" ").filter(Boolean)
  const matchesStatus = (row: LoadListTableRow) => {
    if (filters.status === "all") return true

    if (row.removed) return false

    const hints = rowHints(row)
    const misconfigured = Boolean(hints.errors.length)

    return filters.status === "misconfigured"
      ? misconfigured
      : misconfigured || Boolean(hints.warnings.length)
  }
  const numberInput = filters.numberGreaterThan.trim()
  const numberGreaterThan = numberInput ? Number(numberInput) : null
  const matched = rows.filter((row) => {
    if (!matchesStatus(row)) return false

    if (
      numberGreaterThan !== null &&
      Number.isFinite(numberGreaterThan) &&
      row.rowNumber <= numberGreaterThan
    )
      return false

    const searchText = normalizeSearch(`${row.addressSearchText} ${row.contactSearchText}`)

    const searchWords = new Set(searchText.split(/[^\p{L}\p{N}]+/u))

    return searchTerms.every((term) =>
      /^\d+$/.test(term) ? searchWords.has(term) : searchText.includes(term),
    )
  })
  const matchedIds = new Set(matched.map((row) => row.id))

  return rows.filter(
    (row) =>
      matchedIds.has(row.id) ||
      (matchesStatus(row) &&
        !row.selectedUnitId &&
        row.copyOriginRowId &&
        matchedIds.has(row.copyOriginRowId)),
  )
}

export const openWorkOrderPropertyIdsForRows = ({
  rows,
  loadedPropertyIds = [],
  loadingPropertyIds = [],
}: {
  rows: Pick<LoadListTableRow, "propertyId">[]
  loadedPropertyIds?: string[]
  loadingPropertyIds?: string[]
}): string[] => {
  const skipIds = new Set([...loadedPropertyIds, ...loadingPropertyIds])
  const propertyIds: string[] = []
  for (const row of rows) {
    if (!row.propertyId || skipIds.has(row.propertyId)) continue

    propertyIds.push(row.propertyId)
    skipIds.add(row.propertyId)
  }

  return propertyIds
}
